import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings, MessageSquare, CheckCircle, AlertCircle, Send, Loader2, Upload, Globe } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { globalSettingsApi, queryKeys } from '@/lib/api';

function WebhookInput({ label, description, value, onChange, disabled }: {
  label: string;
  description: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</label>
      <p className="text-xs text-muted-foreground">{description}</p>
      <input
        type="url"
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        placeholder="https://discord.com/api/webhooks/..."
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
      />
    </div>
  );
}

export default function AdminSettingsTab() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.globalSettings.all,
    queryFn: globalSettingsApi.get,
  });

  const [alerts, setAlerts]     = useState('');
  const [activity, setActivity] = useState('');
  const [initialised, setInitialised] = useState(false);

  if (data && !initialised) {
    setAlerts(data.discordAlertsWebhook ?? '');
    setActivity(data.discordActivityWebhook ?? '');
    setInitialised(true);
  }

  const saveMutation = useMutation({
    mutationFn: () => globalSettingsApi.update({
      discordAlertsWebhook:   alerts.trim()   || null,
      discordActivityWebhook: activity.trim() || null,
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.globalSettings.all }),
  });

  const [testResults, setTestResults] = useState<Record<string, string> | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  const handleTest = async () => {
    setIsTesting(true);
    setTestResults(null);
    try {
      const { results } = await globalSettingsApi.testDiscord();
      setTestResults(results);
    } catch {
      setTestResults({ error: 'Request failed' });
    } finally {
      setIsTesting(false);
    }
  };

  const isSaving = saveMutation.isPending;
  const isDirty = data
    ? (alerts.trim() || null) !== data.discordAlertsWebhook ||
      (activity.trim() || null) !== data.discordActivityWebhook
    : false;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Settings className="h-6 w-6" />
          System Settings
        </h2>
        <p className="text-muted-foreground mt-1">Global configuration for notifications and integrations</p>
      </div>

      {/* Discord Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-indigo-500" />
            Discord Notifications
          </CardTitle>
          <CardDescription>
            Configure Discord webhook URLs for alert and activity channels.
            Alerts receive agent offline, stream overdue, and OBS fault messages.
            Activity receives stream started and ended confirmations.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading settings…
            </div>
          ) : (
            <>
              <WebhookInput
                label="#csm-alerts webhook"
                description="Critical alerts, warnings, and recovery messages."
                value={alerts}
                onChange={setAlerts}
                disabled={isSaving}
              />
              <WebhookInput
                label="#csm-activity webhook"
                description="Stream started and stream ended confirmations."
                value={activity}
                onChange={setActivity}
                disabled={isSaving}
              />

              {saveMutation.isSuccess && !isDirty && (
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <CheckCircle className="h-4 w-4" />
                  Settings saved
                </div>
              )}
              {saveMutation.isError && (
                <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                  <AlertCircle className="h-4 w-4" />
                  Failed to save — please try again
                </div>
              )}

              <div className="flex items-center gap-3 pt-1">
                <Button onClick={() => saveMutation.mutate()} disabled={isSaving || !isDirty}>
                  {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : 'Save'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleTest}
                  disabled={isTesting || (!data?.discordAlertsWebhook && !data?.discordActivityWebhook)}
                >
                  {isTesting
                    ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending…</>
                    : <><Send className="mr-2 h-4 w-4" />Send Test Message</>}
                </Button>
              </div>

              {testResults && (
                <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-800">
                  <p className="font-medium mb-1">Test results:</p>
                  {Object.entries(testResults).map(([channel, result]) => (
                    <div key={channel} className="flex items-center gap-2">
                      {result === 'ok'
                        ? <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                        : <AlertCircle className="h-3.5 w-3.5 text-red-500" />}
                      <span className="capitalize">{channel}:</span>
                      <span className={result === 'ok' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                        {result}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Upload Limits */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Limits
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Max file size</p>
              <p className="text-2xl font-bold text-blue-600">10 MB</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Max dimensions</p>
              <p className="text-sm font-bold text-green-600">2048 × 2048</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Min dimensions</p>
              <p className="text-sm font-bold">100 × 100</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Formats</p>
              <div className="flex flex-wrap gap-1">
                {['PNG', 'JPG', 'WEBP', 'SVG'].map(f => (
                  <Badge key={f} variant="outline" className="text-xs">{f}</Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Endpoints */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Key Endpoints
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            {[
              { label: 'Health check',   path: '/health' },
              { label: 'Backend API',    path: '/api' },
              { label: 'Overlay source', path: '/overlay/{facilityId}/{sheetId}' },
              { label: 'Agent API',      path: '/api/agent' },
            ].map(({ label, path }) => (
              <div key={path} className="flex justify-between items-center">
                <span className="text-muted-foreground">{label}</span>
                <code className="bg-muted px-2 py-0.5 rounded text-xs">{path}</code>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
